import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import CustomerHeader from "../../components/CustomerHeader";
import LiveTrackMap from "../../components/LiveTrackMap";
import { toast } from "sonner";
import { Check, Store, Bike, PackageCheck, ArrowLeft, Clock, Truck, Phone } from "lucide-react";

const STEPS = [
  { key: "placed", label: "Order Placed", desc: "We've got your order", icon: PackageCheck },
  { key: "confirmed", label: "Shop Confirmed", desc: "Preparing your items", icon: Store },
  { key: "picked_up", label: "Rider Picked Up", desc: "Rider has your order", icon: Bike },
  { key: "on_the_way", label: "Out for Delivery", desc: "Heading to you", icon: Truck },
  { key: "delivered", label: "Delivered", desc: "Enjoy!", icon: Check },
];

const UPDATE_MSG = {
  placed: "Order placed",
  confirmed: "The shop confirmed your order",
  ready: "Your order is packed and waiting for the rider",
  picked_up: "Your rider picked up the order",
  on_the_way: "Your order is out for delivery",
  delivered: "Delivered. Enjoy!",
  rejected: "The shop couldn't accept this order",
  cancelled: "This order was cancelled",
};

function stepIndex(status) {
  if (status === "placed" || status === "awaiting_payment") return 0;
  if (status === "confirmed" || status === "ready") return 1;
  if (status === "picked_up") return 2;
  if (status === "on_the_way") return 3;
  if (status === "delivered") return 4;
  if (status === "rejected" || status === "cancelled") return -1;
  return 0;
}

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const lastStatus = useRef(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (!active) return;
        if (lastStatus.current && lastStatus.current !== data.status) toast.success(UPDATE_MSG[data.status] || "Order updated");
        lastStatus.current = data.status;
        setOrder(data);
      } catch (e) {
        console.error("Order load failed:", e);
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { active = false; clearInterval(t); };
  }, [id]);

  useEffect(() => {
    if (!order?.created_at) return;
    const start = new Date(order.created_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [order?.created_at]);

  const idx = useMemo(() => (order ? stepIndex(order.status) : 0), [order]);
  const rejected = order?.status === "rejected" || order?.status === "cancelled";
  const delivered = order?.status === "delivered";
  const etaRem = useMemo(() => {
    const eta = 10 * 60;
    return Math.max(0, eta - elapsed);
  }, [elapsed]);

  if (!order) return (
    <div className="light-scope min-h-screen bg-slate-50">
      <CustomerHeader onCartClick={() => {}} />
      <div className="p-10 text-center text-slate-500">Loading order…</div>
    </div>
  );

  const mm = String(Math.floor(etaRem / 60)).padStart(2, "0");
  const ss = String(etaRem % 60).padStart(2, "0");

  return (
    <div className="light-scope min-h-screen bg-slate-50" data-testid="order-tracker-modal">
      <CustomerHeader onCartClick={() => {}} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> All orders</Link>

        <section className="rounded-2xl bg-white border border-slate-200 p-6 pop-in">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Order <span className="mono">#{order.order_id.slice(-6).toUpperCase()}</span></div>
              <h1 className="text-2xl sm:text-3xl font-extrabold mt-1 text-slate-900">{order.shop_name}</h1>
              <div className="text-sm text-slate-500">{order.shop_address}</div>
            </div>
            <div className={`text-right rounded-xl px-4 py-3 ${rejected ? "bg-rose-50 border border-rose-200" : "bg-emerald-50 border border-emerald-200"}`} data-testid="tracker-eta">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{rejected || delivered ? "Status" : "Arriving in"}</div>
              <div className={`text-3xl font-extrabold ${rejected ? "text-rose-700" : "text-emerald-700"}`}>{rejected ? (order.status === "cancelled" ? "Cancelled" : "Rejected") : delivered ? "Delivered" : `${mm}:${ss}`}</div>
            </div>
          </div>

          <div className="relative mt-8">
            <div className="absolute left-[10%] right-[10%] top-5 h-1 bg-slate-200 rounded-full" />
            <div
              className="absolute left-[10%] top-5 h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-700"
              style={{ width: rejected ? "0" : `${(idx / (STEPS.length - 1)) * 80}%` }}
            />
            <div className="relative grid grid-cols-5 gap-1">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = !rejected && i <= idx;
                return (
                  <div key={s.key} className="flex flex-col items-center text-center" data-testid={`tracker-step-${s.key}`}>
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center border-2 transition
                      ${active ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/30" : "bg-white border-slate-300 text-slate-400"}`}>
                      <Icon size={16} />
                      {active && i === idx && !rejected && <span className="absolute inset-0 rounded-full border-2 border-emerald-400 animate-ping" />}
                    </div>
                    <div className={`mt-2 text-[11px] font-bold ${active ? "text-slate-900" : "text-slate-400"}`}>{s.label}</div>
                    <div className={`text-[10px] ${active ? "text-slate-500" : "text-slate-300"}`}>{s.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {order.status === "awaiting_payment" && (
            <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="tracker-awaiting-payment">Waiting for payment confirmation from Razorpay…</div>
          )}

          {order.rider_name && !rejected && !delivered && (
            <div className="mt-6 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="tracker-rider">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 inline-flex items-center justify-center"><Bike size={16} /></div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{order.rider_name} is your rider</div>
                  <div className="text-xs text-slate-500">{order.rider_loc_at ? `Location updated ${new Date(order.rider_loc_at).toLocaleTimeString()}` : "Waiting for rider's live location"}</div>
                </div>
              </div>
              {order.rider_phone && <a href={`tel:${order.rider_phone}`} className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="tracker-call-rider"><Phone size={14} /> Call</a>}
            </div>
          )}

          {!rejected && !delivered && <LiveTrackMap order={order} />}

          <div className="mt-8 border-t border-slate-200 pt-5">
            <div className="font-bold text-slate-900 mb-2">Items ({order.items.length})</div>
            <div className="space-y-2">
              {order.items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3">
                  <img src={it.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{it.name}</div>
                    <div className="text-[11px] text-slate-500">× {it.qty} · ₹{it.price}</div>
                  </div>
                  <div className="font-bold text-sm">₹{(it.price * it.qty).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between text-sm">
            <div className="text-slate-500 inline-flex items-center gap-1.5"><Clock size={14} className="text-emerald-600" /> Placed at {new Date(order.created_at).toLocaleTimeString()}</div>
            <div className="font-extrabold text-slate-900">Total ₹{order.total.toFixed(2)}</div>
          </div>
        </section>
      </main>
    </div>
  );
}
